import { useCallback } from 'react'
import { ApiAccess } from 'features/session/types'
import {
	ApiGetNoToken,
	ApiGetFileNoToken,
  ApiGetWithToken,
	ApiDeleteWithToken,
	ApiPatchWithToken,
	ApiPostWithToken,
	ApiGetFileWithToken,
	ApiPostFileWithToken,
	useApiFetch,
	useApiPost,
	FileResponse
} from 'shared/api'
import {
	notificationApi,
	notificationApiBy,
	openNotificationApi, openNotificationApiBy
} from 'interface/endpoints'
import {
  NotificationResult,
  NotificationResultList,
	NotificationFormData
} from './types'
import { CancelToken } from 'axios'

export * from './types'

export const CountOpenNotification = (
  domain: string
) => ApiGetNoToken<number>(
  openNotificationApi(domain, '/count')
)

export const ListOpenNotification = (
  domain: string
) => ApiGetNoToken<NotificationResultList>(
  openNotificationApi(domain, '/list')
)

export const GetOpenNotificationLogo = (
  domain: string,
  notificationId: number
) => ApiGetFileNoToken(
  openNotificationApiBy(domain, notificationId, '/logo')
)

export const useListNotification = (
	all: boolean
) => useApiFetch(
	useCallback(
		(apiAccess, cancelToken) => {
      return ApiGetWithToken<NotificationResultList>(
        apiAccess,
        notificationApi(all ? '/list/all' : '/list'),
        cancelToken
      )
    },
		[all]
	)
)

export const useGetNotificationUnreadCount = (
) => useApiFetch(
	useCallback(
		(apiAccess, cancelToken) => {
      return ApiGetWithToken<number>(
        apiAccess,
        notificationApi('/unread'),
        cancelToken
  		)
    },
		[]
	)
)

export const usePostNotificationReadIt = (
  callback?: {
    onPosted?: (result: NotificationResult) => void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError?: (reason: any) => void
  }
) => useApiPost<number, NotificationResult>(
	useCallback(
		(apiAccess, cancelToken, notificationId, webSocketId) => {
			return ApiPostWithToken(
				apiAccess,
        notificationApiBy(notificationId, '/readit'),
				undefined,
				cancelToken,
				webSocketId
			)
		},
		[]
	),
	useCallback(
		(result) => {
			if (callback?.onPosted) callback.onPosted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useGetNotification = (
  notificationId?: number | null
) => useApiFetch(
	useCallback(
		(apiAccess, cancelToken) => {
      if (!notificationId) return null
      return ApiGetWithToken<NotificationResult>(
        apiAccess,
        notificationApiBy(notificationId),
        cancelToken
  		)
    },
		[notificationId]
	)
)

const postNotificationLogo = (
	apiAccess: ApiAccess,
	notificationId: number,
	file: File, 
	cancelToken?: CancelToken,
	webSocketId?: string
) => {
	const formData = new FormData()
	formData.append('file', file)
	return ApiPostFileWithToken(
		apiAccess,
		notificationApiBy(notificationId, '/logo'),
		formData,
		cancelToken,
		webSocketId
	)
}

export const usePostNotification = (
  callback?: {
    onPosted?: (result: NotificationResult, logo?: FileResponse) => void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
		onError?: (reason: any) => void
  }
) => useApiPost<{ form: NotificationFormData, file?: File }, NotificationResult>(
	useCallback(
		(apiAccess, cancelToken, { form }, webSocketId) => {
			return ApiPostWithToken(
				apiAccess,
        notificationApi(),
				form,
				cancelToken,
				webSocketId
			)
		},
		[]
	),
	useCallback(
		async (result, { file }, apiAccess, cancelToken) => {
			if (file) {
				const formData = new FormData()
				formData.append('file', file)
				const logo = await postNotificationLogo(apiAccess, result.notificationId, file, cancelToken)
				if (callback?.onPosted) callback.onPosted(result, logo)
			} else {
				if (callback?.onPosted) callback.onPosted(result)
			}
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useEditNotification = (
	notificationId: number,
  callback?: {
    onEditted?: (result: NotificationResult, logo?: FileResponse) => void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError?: (reason: any) => void
  }
) => useApiPost<{ form: NotificationFormData, file?: File }, NotificationResult>(
	useCallback(
		(apiAccess, cancelToken, { form }, webSocketId) => {
			return ApiPatchWithToken(
				apiAccess,
        notificationApiBy(notificationId),
				form,
				cancelToken,
				webSocketId
			)
		},
		[notificationId]
	),
	useCallback(
		async (result, { file }, apiAccess, cancelToken) => {
			if (file) {
				const formData = new FormData()
				formData.append('file', file)
				const logo = await postNotificationLogo(apiAccess, result.notificationId, file, cancelToken)
				if (callback?.onEditted) callback.onEditted(result, logo)
			} else {
				if (callback?.onEditted) callback.onEditted(result)
			}
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useDeleteNotification = (
  callback?: {
    onDeleted?: (notificationId: number) => void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError?: (reason: any) => void
  }
) => useApiPost<number, number>(
	useCallback(
		(apiAccess, cancelToken, notificationId, webSocketId) => {
			return ApiDeleteWithToken(
				apiAccess,
        notificationApiBy(notificationId),
				cancelToken,
				webSocketId
			)
		},
		[]
	),
	useCallback(
		(result) => {
			if (callback?.onDeleted) callback.onDeleted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useGetNotificationLogo = (
	notificationId?: number | null
) => useApiFetch(
	useCallback(
		(apiAccess, cancelToken) => {
			if (!notificationId) return null
			return ApiGetFileWithToken(
				apiAccess,
				notificationApiBy(notificationId, '/logo'),
				cancelToken
			)
		},
		[notificationId]
	)
)
